import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MyInspectionEngagementSummaryDto, GridFilter } from '../../../../model/modelDto';
import { ProgressStatus } from '../../../../model/modelEnums';
import { UnitOfWorkService } from '../../../services/unit-of-work/unit-of-work.service';
import { IsBusyService } from '../../../services/is-busy/is-busy.service';
import { GlobalErrorHandler } from '../../../services/global-error-handler';

const LOADING_KEY = 'phase8-my-assignments:load';
const DEFAULT_LIMIT = 15;

@Component({
    selector: 'app-my-assignments-home',
    templateUrl: './my-assignments-home.component.html',
    styleUrls: ['./my-assignments-home.component.less'],
    standalone: false
})
export class MyAssignmentsHomeComponent implements OnInit, OnDestroy {
    page = 1;
    limit = DEFAULT_LIMIT;
    pageCount = 1;
    rowCount = 0;
    pageLimitOptions = [15, 25, 50, 100];
    currentYear = new Date().getFullYear();
    inspectionYear = this.currentYear;
    inspectionYears: number[] = [];
    engagementCode = '';
    clientName = '';
    progressStatus = '';
    sort = 'clientName';
    sortAscending = true;
    filters: GridFilter[] = [];
    engagements: MyInspectionEngagementSummaryDto[] = [];
    isLoading = false;

    private subscription = new Subscription();

    constructor(
        public uow: UnitOfWorkService,
        private route: ActivatedRoute,
        private router: Router,
        public isBusy: IsBusyService,
        private errorHandler: GlobalErrorHandler
    ) {}

    ngOnInit() {
        this.subscription.add(this.route.queryParams.subscribe(params => {
            this.page = this.numberParam(params.page, 1);
            this.limit = this.numberParam(params.limit, DEFAULT_LIMIT);
            this.load();
        }));
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    get activeEngagements() {
        return this.engagements.slice().sort((left, right) => {
            const a = this.sortValue(left);
            const b = this.sortValue(right);
            const result = typeof a === 'number' && typeof b === 'number'
                ? a - b
                : String(a || '').localeCompare(String(b || ''));
            return this.sortAscending ? result : -result;
        });
    }

    load() {
        this.isLoading = true;
        this.isBusy.set(LOADING_KEY, true, 'Loading your assignments');
        Promise.all([this.getRowCount(), this.getItems(), this.getYears()])
            .then(() => this.pageCount = Math.max(1, Math.ceil(this.rowCount / this.limit)))
            .catch(error => this.errorHandler.handleError(error))
            .finally(() => {
                this.isLoading = false;
                this.isBusy.set(LOADING_KEY, false);
            });
    }

    applyFilters() {
        this.page = 1;
        this.updateFilter('code', this.engagementCode);
        this.updateFilter('client', this.clientName);
        this.updateFilter('year', this.inspectionYear ? String(this.inspectionYear) : '');
        this.updateFilter('progressStatus', this.progressStatus);
        this.load();
    }

    clearFilters() {
        this.engagementCode = '';
        this.clientName = '';
        this.inspectionYear = this.currentYear;
        this.progressStatus = '';
        this.filters = [];
        this.applyFilters();
    }

    setSort(column: string) {
        if (this.sort === column) {
            this.sortAscending = !this.sortAscending;
        } else {
            this.sort = column;
            this.sortAscending = true;
        }
    }

    getSortIcon(column: string) {
        if (this.sort !== column) {
            return 'fa-sort';
        }
        return this.sortAscending ? 'fa-sort-asc' : 'fa-sort-desc';
    }

    getProgressStatusName(status: ProgressStatus) {
        return this.uow.getProgressStatusName(status);
    }

    progressPercent(item: MyInspectionEngagementSummaryDto) {
        const completed = item.myAssignedQuestions != null ? item.myCompletedQuestions : item.completedQuestions;
        const total = item.myAssignedQuestions != null ? item.myAssignedQuestions : item.totalQuestions;
        return total > 0 ? completed / total : 0;
    }

    open(item: MyInspectionEngagementSummaryDto) {
        this.router.navigate(['/my-assignments', 'inspection'], { queryParams: { id: item.engagementId } });
    }

    private getRowCount() {
        this.syncFilters();
        return this.uow.getMyInspectionEngagementsCount(JSON.stringify(this.filters))
            .then(result => this.rowCount = Number(result && result.length ? result[0] : result || 0));
    }

    private getItems() {
        this.syncFilters();
        return this.uow.getMyInspectionEngagements(this.page, this.limit, JSON.stringify(this.filters))
            .then(result => this.engagements = (result || []) as MyInspectionEngagementSummaryDto[]);
    }

    private getYears() {
        return this.uow.getInspectionYears()
            .then(result => this.inspectionYears = (result || []).sort((a, b) => b - a));
    }

    private syncFilters() {
        this.updateFilter('code', this.engagementCode);
        this.updateFilter('client', this.clientName);
        this.updateFilter('year', this.inspectionYear && this.inspectionYear !== 0 ? String(this.inspectionYear) : '');
        this.updateFilter('progressStatus', this.progressStatus);
    }

    private updateFilter(key: string, value: string) {
        const index = this.filters.findIndex(filter => filter.key === key);
        if (!value) {
            if (index >= 0) {
                this.filters.splice(index, 1);
            }
            return;
        }
        if (index >= 0) {
            this.filters[index].value = value;
        } else {
            this.filters.push({ key, value });
        }
    }

    private numberParam(value: any, fallback: number) {
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
    }

    private sortValue(item: MyInspectionEngagementSummaryDto): any {
        switch (this.sort) {
            case 'code': return item.engagementCode || item.code;
            case 'inspectionYear': return item.inspectionYear || (item as any).year;
            case 'progressStatus': return item.progressStatus || item.status;
            default: return item.clientName || (item as any).engagementClient;
        }
    }
}
